/**
 * 权限管理 · 权限矩阵（canvas page-86 对齐）
 * 模块 × 角色 网格 · 每格按该角色在该模块的权限码推导等级（✏可编辑/👁只读/—不可见）
 * 模块按业务分组过滤（资源/商务/运营/系统）+ 图例 · 点格可编辑（CellEditDrawer → PATCH 角色权限，DB-back union_permissions 真生效）
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { rbacApi, type RbacRole } from "@/services/rbac";
import { toFrontendRole } from "@/services/types/auth";
import { PageShell, LoadingState, EmptyState, ErrorState } from "@/shared/ui/PageHeader";
import { RbacTabs } from "./RbacTabs";
import {
  ROLE_META, roleEmoji, MODULE_META, moduleCn, moduleEmoji, moduleGroup,
  GROUP_LABEL, deriveLevel, isWriteCode, LEVEL_META, permLabel, registerServerLabels, type ModuleGroup, type PermKind,
} from "./rbacMeta";

const GROUP_ORDER: (ModuleGroup)[] = ["resource", "business", "ops", "system"];
/** RBAC-OWNER-1 · 技术管理员固定拥有全部权限（防锁死），矩阵里只读；老板和其它内置 / 自定义角色都可以点格修改 */
const FIXED_ROLES = new Set(["tech_admin"]);
const FIXED_NOTE = "技术管理员固定拥有全部权限，不能修改";

export function RolesPermissionsPage() {
  const qc = useQueryClient();
  const rolesQ = useQuery({ queryKey: ["rbac", "roles"], queryFn: rbacApi.listRoles });
  const permsQ = useQuery({
    queryKey: ["rbac", "permissions"],
    queryFn: () => rbacApi.listPermissions().then((p) => { registerServerLabels(p); return p; }),
  });
  // RBAC-ENF1 · 保存后提示：权限改动在服务器端约 30 秒内生效；已登录的人界面上的菜单 / 按钮要重新登录才刷新
  const [savedNotice, setSavedNotice] = useState(false);
  const [group, setGroup] = useState<"all" | ModuleGroup>("all");
  // 点格编辑（canvas page-86）：选中 (角色×模块)
  const [edit, setEdit] = useState<{ role: RbacRole; module: string } | null>(null);

  const [sp] = useSearchParams();
  const focusRole = sp.get("role"); // 从角色管理「编辑权限」带入 → 聚焦单角色列

  const sortedRoles: RbacRole[] = useMemo(() => {
    const order = Object.keys(ROLE_META);
    const idx = (c: string) => (c.startsWith("custom_") ? -1 : order.indexOf(toFrontendRole(c)));
    return [...(rolesQ.data ?? [])].sort((a, b) => idx(a.code) - idx(b.code));
  }, [rolesQ.data]);

  // 聚焦模式：矩阵只显示该角色列（其余逻辑不变）
  const matrixRoles = useMemo(
    () => (focusRole ? sortedRoles.filter((r) => r.code === focusRole) : sortedRoles),
    [sortedRoles, focusRole],
  );
  const focused = focusRole ? sortedRoles.find((r) => r.code === focusRole) : null;

  // RBAC-UX-1 · 权限码 → 读写分类（后端逐条标的 kind）
  const kindMap = useMemo(() => {
    const m: Record<string, PermKind | null | undefined> = {};
    for (const p of permsQ.data ?? []) m[p.code] = p.kind;
    return m;
  }, [permsQ.data]);

  const codeToModule = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of permsQ.data ?? []) m[p.code] = p.module;
    return m;
  }, [permsQ.data]);

  // 全部模块（按分组排序）
  const modules = useMemo(() => {
    const set = new Set((permsQ.data ?? []).map((p) => p.module));
    return Array.from(set).sort((a, b) => {
      const ga = GROUP_ORDER.indexOf(moduleGroup(a)), gb = GROUP_ORDER.indexOf(moduleGroup(b));
      if (ga !== gb) return ga - gb;
      const oa = Object.keys(MODULE_META).indexOf(a), ob = Object.keys(MODULE_META).indexOf(b);
      return oa - ob;
    });
  }, [permsQ.data]);

  // role.code → Set(权限码)
  const rolePermSets = useMemo(() => {
    const m: Record<string, Set<string>> = {};
    for (const r of sortedRoles) m[r.code] = new Set(r.permission_codes);
    return m;
  }, [sortedRoles]);

  if (rolesQ.isLoading || permsQ.isLoading) return <LoadingState />;
  if (rolesQ.isError || permsQ.isError)
    return <ErrorState message="权限矩阵加载失败" onRetry={() => { rolesQ.refetch(); permsQ.refetch(); }} />;
  if (!rolesQ.data || !permsQ.data) return <EmptyState message="无数据" />;

  const visibleModules = group === "all" ? modules : modules.filter((m) => moduleGroup(m) === group);
  const cellCount = visibleModules.length * matrixRoles.length;

  const levelFor = (roleCode: string, module: string) => {
    const codes = (permsQ.data ?? [])
      .filter((p) => p.module === module && rolePermSets[roleCode]?.has(p.code))
      .map((p) => p.code);
    return deriveLevel(codes, (c) => kindMap[c]);
  };

  return (
    <PageShell
      title="权限矩阵"
      subtitle={`${modules.length} 模块 × ${sortedRoles.length} 角色 = ${modules.length * sortedRoles.length} 权限格`}
    >
      <RbacTabs />

      {/* 角色汇总卡（点击聚焦该角色）*/}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {sortedRoles.map((r) => (
          <Link
            key={r.code}
            to={`/admin/permissions/matrix?role=${r.code}`}
            className={`rounded-xl border px-4 py-3 transition ${focusRole === r.code ? "border-blue-400 bg-blue-50 ring-1 ring-blue-200" : "border-slate-200 bg-white hover:bg-slate-50"}`}
          >
            <p className="flex items-center gap-1 text-[14px] font-semibold text-slate-900">
              {roleEmoji(r.code)} {r.label_cn}
              {r.kind === "custom" && <span data-testid={`custom-tag-${r.code}`} className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">自定义</span>}
              {FIXED_ROLES.has(r.code) && <span data-testid={`fixed-tag-${r.code}`} title={FIXED_NOTE} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">🔒 固定全部</span>}
            </p>
            <p className="text-[11px] text-slate-400">{r.permission_codes.length} 权限点</p>
          </Link>
        ))}
      </div>

      {/* 分组过滤 + 图例 */}
      <div className="flex flex-wrap items-center gap-2">
        {(["all", ...GROUP_ORDER] as const).map((g) => {
          const n = g === "all" ? modules.length : modules.filter((m) => moduleGroup(m) === g).length;
          return (
            <button
              key={g}
              type="button"
              onClick={() => setGroup(g)}
              className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium ${group === g ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {GROUP_LABEL[g]} ({n})
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          <span>{LEVEL_META.edit.icon} 可编辑</span>
          <span>{LEVEL_META.read.icon} 只读</span>
          <span className="text-slate-300">— 不可见</span>
        </div>
      </div>

      {focused && (
        <div className="flex items-center justify-between rounded-lg border border-blue-300 bg-blue-50 px-4 py-2">
          <span className="text-[13px] text-blue-900">
            正在聚焦编辑：<span className="font-semibold">{roleEmoji(focused.code)} {focused.label_cn}</span>
            <span className="ml-1 text-[12px] text-blue-600">· {focused.permission_codes.length} 权限点</span>
          </span>
          {FIXED_ROLES.has(focused.code) && <span data-testid="fixed-focus-note" className="text-[12px] font-medium text-slate-600">🔒 {FIXED_NOTE}（只读）</span>}
          <Link to="/admin/permissions/matrix" className="text-[12px] font-medium text-blue-600 hover:underline">查看全部角色 ×</Link>
        </div>
      )}

      {savedNotice && (
        <div role="status" data-testid="relogin-hint" className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-[12px] text-amber-900">
          <span>
            ✅ 权限已保存，服务器约 30 秒内生效。<b>相关账号需要重新登录</b>，他们界面上的菜单和按钮才会按新权限刷新。
          </span>
          <button type="button" onClick={() => setSavedNotice(false)} className="shrink-0 text-amber-700 hover:underline">知道了</button>
        </div>
      )}
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-[12px] text-blue-800">
        点 (角色 × 模块) 格 → 勾选该角色在该模块的权限码 · 保存即生效（影响该角色所有用户的实际访问）
      </div>
      <div data-testid="fixed-role-note" className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-[12px] text-slate-600">
        🔒 技术管理员固定拥有全部权限（防止把自己锁在系统外），这一列只读；老板和其它角色都可以修改——想让老板看不到的技术类功能，取消老板在对应模块的权限即可。
      </div>

      {/* 模块 × 角色 矩阵 */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[12px]" style={{ minWidth: 720 }}>
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr>
              <th className="w-44 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">模块 / 角色</th>
              {matrixRoles.map((r) => (
                <th key={r.code} className="px-2 py-3 text-center text-[10px] font-semibold text-slate-500" style={{ minWidth: 64 }}>
                  <span title={r.label_cn}>{roleEmoji(r.code)}</span>
                  <span className="mt-0.5 block font-normal text-slate-400">{r.label_cn}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleModules.map((m) => (
              <tr key={m} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-1.5 font-medium text-slate-700">
                    <span>{moduleEmoji(m)}</span>{moduleCn(m)}
                    <span className="text-[10px] text-slate-300">{GROUP_LABEL[moduleGroup(m)].replace(/^.\s?/, "")}</span>
                  </span>
                </td>
                {matrixRoles.map((r) => {
                  const lv = levelFor(r.code, m);
                  const meta = LEVEL_META[lv];
                  if (FIXED_ROLES.has(r.code)) {
                    return (
                      <td key={r.code} className="px-2 py-2.5 text-center">
                        <span data-testid={`cell-fixed-${r.code}-${m}`} title={`${r.label_cn} · ${moduleCn(m)} · ${FIXED_NOTE}`} className="inline-block cursor-not-allowed rounded px-2 py-1">
                          <span className={`text-[14px] ${meta.cls}`}>{lv === "none" ? "—" : meta.icon}</span>
                        </span>
                      </td>
                    );
                  }
                  return (
                    <td key={r.code} className="px-2 py-2.5 text-center">
                      <button
                        data-testid={`cell-${r.code}-${m}`}
                        type="button"
                        onClick={() => setEdit({ role: r, module: m })}
                        title={`${r.label_cn} · ${moduleCn(m)} · ${meta.label}（点击编辑）`}
                        className="rounded px-2 py-1 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200"
                      >
                        <span className={`text-[14px] ${meta.cls}`}>{lv === "none" ? "—" : meta.icon}</span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">显示 {visibleModules.length} 模块 × {matrixRoles.length} 角色 = {cellCount} 格</p>

      {edit && (
        <CellEditDrawer
          role={edit.role}
          module={edit.module}
          modulePerms={(permsQ.data ?? []).filter((p) => p.module === edit.module)}
          kindOf={(c) => kindMap[c]}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); setSavedNotice(true); qc.invalidateQueries({ queryKey: ["rbac", "roles"] }); }}
        />
      )}
    </PageShell>
  );
}

/** 点格编辑 drawer：勾选该角色在该模块的权限码（canvas page-86）*/
function CellEditDrawer({
  role, module, modulePerms, kindOf, onClose, onSaved,
}: {
  role: RbacRole;
  module: string;
  modulePerms: { code: string; description: string | null }[];
  kindOf?: (code: string) => PermKind | null | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const moduleCodes = modulePerms.map((p) => p.code);
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(role.permission_codes.filter((c) => moduleCodes.includes(c))),
  );
  const save = useMutation({
    mutationFn: () => {
      // 角色新全集 = 原全集去掉本模块所有码 + 本次勾选的本模块码
      const keepOther = role.permission_codes.filter((c) => !moduleCodes.includes(c));
      const next = [...keepOther, ...Array.from(checked)];
      return rbacApi.updateRolePermissions(role.code, next);
    },
    onSuccess: onSaved,
  });
  const toggle = (code: string) =>
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(code) ? n.delete(code) : n.add(code);
      return n;
    });
  const allOn = checked.size === moduleCodes.length;

  // RBAC-UX-1 · 一键切三档：无权限 = 全取消；只读 = 只勾本模块全部 read 码；编辑 = 全勾。点按钮只改勾选，仍要点底部「保存」才生效。
  const readCodes = moduleCodes.filter((c) => !isWriteCode(c, kindOf));
  const sameAs = (codes: string[]) => checked.size === codes.length && codes.every((c) => checked.has(c));
  const noWriteCode = readCodes.length === moduleCodes.length;      // 本模块全是只读码（编辑 = 只读）
  const noReadCode = readCodes.length === 0;                         // 本模块没有只读码（只读 = 无权限）
  const current: "none" | "read" | "edit" | "custom" =
    checked.size === 0 ? "none"
      : sameAs(moduleCodes) ? (noWriteCode ? "read" : "edit")
        : !noReadCode && sameAs(readCodes) ? "read"
          : "custom";
  const pickLevel = (lv: "none" | "read" | "edit") => setChecked(new Set(lv === "none" ? [] : lv === "read" ? readCodes : moduleCodes));
  const LEVELS = [
    { lv: "none" as const, label: "无权限", disabled: false, hint: "本模块的权限全部取消" },
    { lv: "read" as const, label: "只读", disabled: noReadCode, hint: noReadCode ? "本模块没有只读权限点" : "只勾本模块全部「只看」的权限点" },
    { lv: "edit" as const, label: "编辑", disabled: noWriteCode, hint: noWriteCode ? "本模块只有只读权限点（编辑 = 只读）" : "本模块全部勾上" },
  ];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{roleEmoji(role.code)} {role.label_cn} · {moduleEmoji(module)} {moduleCn(module)}</h3>
            <p className="mt-0.5 text-[12px] text-slate-400">勾选该角色在本模块的权限 · {checked.size}/{moduleCodes.length}</p>
          </div>
          <button type="button" onClick={onClose}><X className="h-4 w-4 text-slate-400 hover:text-slate-600" /></button>
        </div>

        <div className="flex items-center gap-2 border-b border-slate-50 px-6 py-2.5" data-testid="level-switch" role="group" aria-label="一键设置本模块权限等级">
          {LEVELS.map((x) => (
            <button
              key={x.lv}
              type="button"
              disabled={x.disabled}
              title={x.hint}
              aria-pressed={current === x.lv}
              data-testid={`level-${x.lv}`}
              onClick={() => pickLevel(x.lv)}
              className={`h-8 flex-1 rounded-lg border text-[12.5px] font-medium transition ${current === x.lv ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"} disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white`}
            >
              {x.label}
            </button>
          ))}
          {current === "custom" && <span data-testid="level-custom" className="shrink-0 text-[12px] font-medium text-amber-600">自定义</span>}
        </div>
        <div className="flex items-center justify-between border-b border-slate-50 px-6 py-2">
          <span className="text-[12px] text-slate-500">{moduleCodes.length} 个权限码</span>
          <button
            type="button"
            onClick={() => setChecked(allOn ? new Set() : new Set(moduleCodes))}
            className="text-[12px] font-medium text-blue-600 hover:underline"
          >
            {allOn ? "全部取消" : "全选"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          <ul className="space-y-1">
            {modulePerms.map((p) => (
              <li key={p.code}>
                <label className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <input type="checkbox" checked={checked.has(p.code)} onChange={() => toggle(p.code)} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="text-[13px] font-medium text-slate-800">{permLabel(p.code)}</span>
                    <code className="block text-[10px] text-slate-400">{p.code}</code>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <p data-testid="drawer-relogin-note" className="mx-6 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          保存后马上对这个角色生效；已登录的同事需要<b>重新登录</b>才按新权限显示菜单和按钮。
        </p>
        {save.isError && <p className="px-6 text-[12px] text-rose-600">保存失败 · 请重试</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">取消</button>
          <button type="button" disabled={save.isPending} onClick={() => save.mutate()} className="h-9 rounded-lg bg-blue-600 px-5 text-[13px] font-semibold text-white disabled:opacity-50">
            {save.isPending ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
